/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useContext, useState } from "react";
import Page from "components/common_components/Page";
import FormsContext from "./FormsContext";
import { useHistory, useParams } from "react-router-dom";
import Button from "components/common_components/Button";
import { hasElements } from "utils/arrays";
import { useDispatch } from "react-redux";
import { notifyError, notifySuccess } from "redux/actions/Notifications";
import Status from "components/common_components/Status";
import LoadingPage from "components/common_components/LoadingPage";
import Icon from "components/common_components/Icon";
import PdfForm from "./components/PdfForm";
import AttorneySelect from "./components/AttorneySelect";
import FileCaseSelect from "./components/FileCaseSelect";
import useFormPreloaders from "./hooks/useFormPreloaders";
import { getFormattedValue, toAddendumsPayload } from "./utils";

const EditForm = () => {
    const { id } = useParams();
    const history = useHistory();
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const [formClient, setFormClient] = useState(undefined);
    const { FormClients, InvoicingIssuers, setStatus, getAttorneyUsersOptions, getFileUrlAsync, pdfUrl } = useContext(FormsContext);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formValues, setFormValues] = useState({});
    const [addendums, setAddendums] = useState([]);
    const {
        clientValues,
        fieldTriggers,
        loadClientPreloader,
        setAttorneyPreloader,
        loadDocIssuerPreloader,
        initFieldTriggers,
        preloadData,
    } = useFormPreloaders();

    useEffect(() => {
        if (id) {
            setIsLoading(true);
            FormClients.getOneById({ id, onSuccess: onLoadSuccess, onError: onLoadError });
        } else {
            dispatch(notifyError("No ID included in the path."));
            setFormClient(undefined);
            history.push("/forms");
        }
    }, [id]);

    const onLoadSuccess = (data) => {
        setFormClient(data);
        setIsLoading(false);
    };

    const onLoadError = (error) => {
        dispatch(notifyError(error.message || error));
        setIsLoading(false);
        history.push(`/forms`);
    };

    useEffect(() => {
        if (formClient) {
            const { values, form } = formClient;
            const fValues = values.reduce((obj, formClientValue) => {
                const { value, formFieldMapping } = formClientValue;
                const { pdfField, type } = formFieldMapping;
                return {
                    ...obj,
                    [pdfField]: getFormattedValue(type, value),
                };
            }, {});
            setFormValues(fValues);
            setAddendums(formClient.addendums || []);

            // Load the PDF file
            getFileUrlAsync(form);

            // Load client preloader
            loadClientPreloader(formClient.client.id, FormClients);

            // Load attorney preloader
            const attorneyUser = getAttorneyUsersOptions().find((x) => x.id === formClient.attorneyUserId);
            setAttorneyPreloader(attorneyUser?.userAttorney);

            // Load doc issuer preloader
            loadDocIssuerPreloader(InvoicingIssuers);

            initFieldTriggers(formClient.form?.formFieldMappings);
        }
    }, [formClient]);

    const submitForm = (event, formNewValues) => {
        event.preventDefault();

        const mappingsWithValues = Object.keys(formNewValues).map((key) => {
            const value = formNewValues[key];
            const id = formClient.form.formFieldMappings.find((x) => x.pdfField === key)?.id;
            return {
                id,
                value,
            };
        });

        if (formClient.form && formClient.client && hasElements(mappingsWithValues)) {
            setIsSubmitting(true);

            const oldValues = formClient.values;
            const newValues = mappingsWithValues.reduce((arr, mapping) => {
                const oldValue = oldValues.find((x) => x.fieldMappingsId === mapping.id);
                const valueObj = {
                    id: oldValue ? oldValue.id : null,
                    formClientId: formClient.id,
                    fieldMappingsId: mapping.id,
                    value: mapping.value,
                };
                return [...arr, valueObj];
            }, []);

            const payload = {
                ...formClient,
                values: newValues,
                addendums: toAddendumsPayload(addendums),
            };

            FormClients.update({
                payload,
                onSuccess: onEditSuccess,
                onError: onEditError,
            });
        }
    };

    const onEditSuccess = () => {
        dispatch(notifySuccess("Form updated successfully"));
        setIsSubmitting(false);
        history.push(`/forms/${id}`);
    };

    const onEditError = (error) => {
        dispatch(notifyError(error.message || error));
        setIsSubmitting(false);
    };

    const onPreloadData = () => preloadData(formClient?.form?.formFieldMappings);

    const saveAsCompleted = () => {
        setStatus(formClient.id, "completed", onEditSuccess);
    };

    const saveAsDraft = () => {
        setStatus(formClient.id, "draft", onEditSuccess);
    };

    const onCancelHandler = () => history.push(`/forms`);

    return (
        <Page className="edit-form-page">
            {isLoading ? (
                <LoadingPage />
            ) : (
                <>
                    {formClient && (
                        <>
                            <div className="form-head">
                                <div className="item form-status">
                                    <Status id={formClient.status} withIcon />
                                </div>
                                <div className="item">{formClient.form?.category?.category}</div>
                                <div className="item">
                                    {formClient.form?.code}
                                    <span>({formClient.form?.version})</span>
                                    {formClient.form?.isCurrent ? <Status id="active" /> : <Status id="oldVersion" />}
                                </div>
                                <div className="item">{formClient.client?.fullName}</div>
                                <div className="item form-actions">
                                    <Button secondary small onClick={onPreloadData}>
                                        <Icon name="client-info" size="sm" color="white" />
                                        <span>Preload data</span>
                                    </Button>
                                    <Button secondary small type="submit" loading={isSubmitting} formId="pdf-overlay-form">
                                        <Icon name="save" size="sm" color="white" />
                                        <span>Save</span>
                                    </Button>

                                    {formClient?.status !== "draft" && (
                                        <Button secondary small onClick={saveAsDraft}>
                                            <Icon name="draft" size="sm" color="white" />
                                            <span>Mark as draft</span>
                                        </Button>
                                    )}

                                    {formClient?.status !== "completed" && (
                                        <Button secondary small onClick={saveAsCompleted}>
                                            <Icon name="complete-doc" size="sm" color="white" />
                                            <span>Mark as completed</span>
                                        </Button>
                                    )}

                                    <Button secondary outline small className="cancel" onClick={onCancelHandler}>
                                        Cancel
                                    </Button>
                                </div>
                            </div>
                            <div className="form-head">
                                <AttorneySelect
                                    value={getAttorneyUsersOptions().find((x) => x.id === formClient.attorneyUserId) || null}
                                    options={getAttorneyUsersOptions()}
                                    onChange={(user) => {
                                        setAttorneyPreloader(user?.userAttorney);
                                        setFormClient((prev) => ({ ...prev, attorneyUserId: user?.id }));
                                    }}
                                />
                                <FileCaseSelect
                                    clientId={formClient.client?.id}
                                    value={formClient}
                                    onChange={(fileCase) =>
                                        setFormClient((prev) => ({
                                            ...prev,
                                            module: fileCase?.module || null,
                                            fileCaseId: fileCase?.fileCaseId || null,
                                            fileNo: fileCase?.fileNo || null,
                                        }))
                                    }
                                />
                            </div>
                        </>
                    )}
                    <PdfForm
                        pdfUrl={pdfUrl}
                        formValues={formValues}
                        clientValues={clientValues}
                        onSubmit={submitForm}
                        fieldTriggers={fieldTriggers}
                        fieldMappings={formClient?.form?.formFieldMappings}
                        addendumInfo={{ formCode: formClient?.form?.code }}
                        addendums={addendums}
                        onAddendumsChange={setAddendums}
                    />
                </>
            )}
        </Page>
    );
};

export default EditForm;
